import React, { useEffect, useState, useCallback } from 'react';

const PasswordStrengthIndicator = ({ password, onStrengthChange }) => {
  const [strength, setStrength] = useState(null);

  const calculateStrength = useCallback((pwd) => {
    let score = 0;
    const criteria = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      numbers: /\d/.test(pwd),
      symbols: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
      noCommon: !isCommonPassword(pwd)
    };

    Object.values(criteria).forEach(met => {
      if (met) score += 1;
    });

    return {
      score,
      level: getStrengthLevel(score),
      criteria,
      message: getStrengthMessage(score, criteria)
    };
  }, []);

  useEffect(() => {
    if (password) {
      const newStrength = calculateStrength(password);
      setStrength(newStrength);
      if (onStrengthChange) {
        onStrengthChange(newStrength);
      }
    } else {
      setStrength(null);
      if (onStrengthChange) {
        onStrengthChange(null);
      }
    }
  }, [password, onStrengthChange, calculateStrength]);

  const isCommonPassword = (pwd) => {
    const common = [
      'password', '123456', '123456789', 'qwerty', 'abc123', 
      'password123', 'admin', 'letmein', 'welcome', 'monkey'
    ];
    return common.includes(pwd.toLowerCase());
  };

  const getStrengthLevel = (score) => {
    if (score < 3) return 'weak';
    if (score < 5) return 'medium';
    return 'strong';
  };

  const getStrengthMessage = (score, criteria) => {
    const missing = [];
    if (!criteria.length) missing.push('at least 8 characters');
    if (!criteria.uppercase) missing.push('uppercase letter');
    if (!criteria.lowercase) missing.push('lowercase letter');
    if (!criteria.numbers) missing.push('number');
    if (!criteria.symbols) missing.push('special character');
    if (!criteria.noCommon) missing.push('avoid common passwords');

    if (missing.length === 0) return 'Strong password!';
    return `Add: ${missing.join(', ')}`;
  };

  const getColor = () => {
    if (!strength) return '#cccccc';
    switch (strength.level) {
      case 'weak': return '#ff4444';
      case 'medium': return '#ffaa00';
      case 'strong': return '#00aa00';
      default: return '#cccccc';
    }
  };

  if (!password || !strength) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ 
        width: '100%', 
        height: '4px', 
        backgroundColor: '#e0e0e0', 
        borderRadius: '2px',
        marginBottom: '4px'
      }}>
        <div 
          style={{ 
            width: `${strength ? (strength.score / 6) * 100 : 0}%`, 
            height: '100%', 
            backgroundColor: getColor(),
            borderRadius: '2px',
            transition: 'all 0.3s ease'
          }}
        />
      </div>
      <div style={{ 
        fontSize: '12px', 
        color: getColor(),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>{strength ? strength.level.toUpperCase() : ''}</span>
        <span style={{ color: '#666', fontSize: '11px' }}>
          {strength ? strength.message : ''}
        </span>
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;